import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { CustomDrawer } from '../commons/CustomDrawer';
import { userPushNotificationsApi } from '../../services/apiServices/user/userPushNotifications';
import './PushNotifications.scss';
import { PushNotification } from '../../models/PushNotification';
import { Notification } from './Notification';
import { BellIcon } from '../commons/icons/Bell';
import DateVO from '../../core/valueObjects/DateVO';
import { useDebounce } from '../../hooks/useDebounce';
import { useCustomSnackbar } from '../../hooks/useCustomEnqueueSnackbar';
import { NotificationSkeleton } from '../commons/NotificationSkeleton';
import { useAppSelector } from '../../hooks/redux';
import { getErrorMessage } from '../../utils';

interface PushNotificationsProps {
  pushNotifications: PushNotification[] | undefined;
  isNotificationsLoading: boolean;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
  getMessages: (_: string) => void;
  handleClose: () => void;
  setIsNewMessages: (isNew: boolean) => void;
}

export const PushNotifications: FC<PushNotificationsProps> = ({
  pushNotifications,
  isNotificationsLoading,
  nextPageUrl,
  prevPageUrl,
  getMessages,
  handleClose,
  setIsNewMessages,
}) => {
  const { isOpenPushNotifications } = useAppSelector((state) => state.appReducer);
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const [seenNotificationsId, setSeenNotificationsId] = useState<string[]>([]);
  const [isRendered, setIsRendered] = useState(false);
  const [loadingPage, setLoadingPage] = useState(0);
  const [markAsReadApi] = userPushNotificationsApi.useMarkNotificationsAsReadMutation();
  const { t } = useTranslation();
  const newMessageRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const notificationsParent = useRef<HTMLDivElement | null>(null);
  const markAsReadNotifications = useDebounce(
    useCallback(async () => {
      if (seenNotificationsId.length) {
        try {
          await markAsReadApi({ messageIDs: seenNotificationsId })
            .unwrap()
            .then(() => setSeenNotificationsId([]));
        } catch (err) {
          const message = getErrorMessage(err);
          customEnqueueSnackbar(message, 'error');
        }
      }
    }, [seenNotificationsId]),
    1000,
  );

  const getFormatDate = (date: Date | null) => {
    if (date) {
      const day = date.getUTCDate();
      const month = date.getUTCMonth() + 1;
      const year = date.getUTCFullYear();

      return `${day} ${month} ${year}`;
    }
    return '';
  };

  const validateCreateNotificationDate = (notifications: PushNotification[]) => {
    let prevDate: Date | null = null;
    return notifications.map((el) => {
      const createDate = el.CreateDate ? DateVO.create(el.CreateDate).get() : null;
      if (getFormatDate(createDate) !== getFormatDate(prevDate)) {
        prevDate = createDate;
        return { ...el, createdAt: el.CreateDate };
      }
      return { ...el, CreateDate: null, createdAt: el.CreateDate };
    });
  };

  const findFirstUnreadNotification = useCallback(
    (notifications: PushNotification[] | undefined) => {
      if (notifications) {
        const reversedArray = [...notifications].reverse();
        return reversedArray.find((el) => el.ReadDate === null);
      }
      return null;
    },
    [pushNotifications],
  );

  const onDrawerScroll = (e) => {
    const el = e.target;
    if (el.scrollTop < 850 && nextPageUrl) {
      const params = new URLSearchParams(new URL(nextPageUrl).search);
      const page = Number(params.get('page'));
      const perPage = Number(params.get('per_page'));
      if (notificationsParent.current?.childElementCount === perPage * (page - 1)) {
        setLoadingPage(page);
        getMessages(`${page}`);
      }
    }
  };

  const handleCloseDrawer = () => {
    if (containerRef.current) {
      containerRef.current.parentElement?.removeEventListener('scroll', onDrawerScroll);
    }
    setLoadingPage(0);
    handleClose();
  };

  useEffect(() => {
    if (containerRef.current && nextPageUrl) {
      containerRef.current.parentElement?.addEventListener('scroll', onDrawerScroll);
    }
    return () => {
      containerRef.current?.parentElement?.removeEventListener('scroll', onDrawerScroll);
    };
  }, [containerRef.current, nextPageUrl]);

  useEffect(() => {
    if (notificationsParent.current?.childElementCount) {
      const params = new URLSearchParams(new URL(prevPageUrl || nextPageUrl || '').search);
      const perPage = Number(params.get('per_page'));
      const index = notificationsParent.current.childElementCount % perPage || perPage;
      notificationsParent.current?.children[index]?.scrollIntoView({ block: 'center' });
    }
  }, [pushNotifications?.length]);

  useEffect(() => {
    setIsNewMessages(!!findFirstUnreadNotification(pushNotifications));
  }, [pushNotifications]);

  useEffect(() => {
    setIsRendered(!isRendered);
  }, [isOpenPushNotifications]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView();
  }, [isRendered]);

  useEffect(() => {
    markAsReadNotifications();
  }, [markAsReadNotifications]);

  const notificationClassName = `push-notifications__content ${
    pushNotifications && pushNotifications.length ? '' : 'push-notifications__content_full-height'
  }`;

  return (
    <CustomDrawer isOpen={isOpenPushNotifications} handleClose={handleCloseDrawer} size="notifications">
      <div className="push-notifications" ref={containerRef}>
        <div className="push-notifications__header">
          <h3>{t('notifications.notifications')}</h3>
          <div className="push-notifications__header__close-button">
            <IconButton onClick={handleCloseDrawer} aria-label="close" size="small">
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className={notificationClassName} ref={notificationsParent}>
          {isNotificationsLoading && loadingPage ? (
            <>
              <NotificationSkeleton />
              <NotificationSkeleton />
              <NotificationSkeleton />
            </>
          ) : (
            <>
              {pushNotifications && pushNotifications.length ? (
                validateCreateNotificationDate([...pushNotifications].reverse()).map((item) => {
                  return (
                    <Notification
                      newMessageRef={newMessageRef}
                      key={item.id}
                      setSeenNotificationsId={setSeenNotificationsId}
                      seenNotificationsId={seenNotificationsId}
                      firstUnreadNotificationId={findFirstUnreadNotification(pushNotifications)?.id || 0}
                      handleClose={handleCloseDrawer}
                      {...item}
                    />
                  );
                })
              ) : (
                <div className="push-notifications__content_empty">
                  <BellIcon />
                  <p>{t('notifications.youDontHaveNotifications')}</p>
                </div>
              )}
            </>
          )}
        </div>
        <div ref={bottomRef} />
      </div>
    </CustomDrawer>
  );
};
